import { inject, Injectable, NgZone } from '@angular/core';
import { LoaderWithWordsService } from '@app/shared';
import { environment } from '@env/environment';
import { BaseService, HttpOptions, LoginResponse } from '@makiwin/ngx-drupal8-rest';
import { map, mergeMap, Observable } from 'rxjs';
import { DeviceDetectService } from '../device-detect/device-detect.service';
declare const google: any;
declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class GoogleLoginService extends BaseService {
  private readonly deviceDetectService: DeviceDetectService = inject(DeviceDetectService);
  private readonly loaderWords: LoaderWithWordsService = inject(LoaderWithWordsService);
  private readonly zone: NgZone = inject(NgZone);

  /**
   * Initiates the Google login process for both Cordova and web environments.
   *
   * - On Cordova devices, uses the `window.plugins.googlesignin` plugin to authenticate and obtain an access token.
   * - On web, uses the Google OAuth2 Token Client to request an access token.
   * - Handles UI loader visibility during the login process.
   * - Emits a `LoginResponse` on successful authentication, or an error if authentication fails.
   *
   * @param $event Optional event to prevent default browser behavior during login.
   * @returns An `Observable` that emits a `LoginResponse` on success, or an error on failure.
   */
  executeLogin($event?: Event): Observable<LoginResponse | undefined> {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
      $event.stopImmediatePropagation();
    }
    this.loaderWords.show();
    return new Observable<LoginResponse | undefined>((observer) => {
      if (this.deviceDetectService.isCordova) {
        window.plugins.googlesignin.login(
          {
            webClientId: environment.googleClientId,
          },
          (res: any) => {
            this.zone.run(() => {
              if (res.accessToken) {
                this.loginWithGoogleToken(res.accessToken).subscribe({
                  next: (loginResponse: LoginResponse) => {
                    observer.next(loginResponse);
                    observer.complete();
                  },
                  error: (err) => {
                    observer.error(err);
                    this.loaderWords.hide();
                  },
                });
              } else {
                console.error('Token response error:', res);
                observer.error('Error on login with Google');
              }
            });
          },
          (err: any) => {
            this.zone.run(() => {
              this.loaderWords.hide();
              console.error('Google authentication error:', err);
              observer.error('Error on login with Google: ' + err?.toString());
            });
          }
        );
      } else {
        this.loaderWords.show();
        google.accounts.oauth2
          .initTokenClient({
            client_id: environment.googleClientId,
            scope: 'https://www.googleapis.com/auth/userinfo.profile', // or any scopes you need
            callback: (response: any) => {
              if (response.access_token) {
                this.loaderWords.show();

                this.loginWithGoogleToken(response.access_token).subscribe({
                  next: (loginResponse: LoginResponse) => {
                    observer.next(loginResponse);
                    observer.complete();
                  },
                  error: (err) => {
                    this.loaderWords.hide();
                    observer.error(err), this.loaderWords.hide();
                  },
                });
                // You can now call Google APIs with this token
              } else {
                this.loaderWords.hide();
                console.error('Token response error:', response);
                observer.error(new Error('Error on login with Google'));
              }
            },
          })
          .requestAccessToken();
      }
    });
  }

  /**
   * Authenticates a user using a Google access token and retrieves a login response.
   *
   * This method sends a POST request to the Google login endpoint with the provided
   * access token. Upon successful login, it retrieves a token and saves the connection
   * details for future use.
   *
   * @param accessToken - The Google access token used for authentication.
   * @returns An observable that emits the login response upon successful authentication.
   */
  loginWithGoogleToken(accessToken: string): Observable<LoginResponse> {
    const httpOptions: HttpOptions = {
      method: 'post',
    };
    return this.request(httpOptions, '/user/login/google?_format=json', { access_token: accessToken }).pipe(
      mergeMap((response: LoginResponse) => {
        return this.getToken().pipe(
          map((token: string) => {
            // Save the response connection
            this.saveConnection(response, token);
            return response;
          })
        );
      })
    );
  }
}
